import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit3, Archive, Play } from 'lucide-react';
import { backofficeApi, formatCurrency, formatNumber, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { PassPlanDto, PassPlanRequestDto, BillingPeriod, WeekdayCode } from '@kardume/types';
import { WEEKDAYS } from '@kardume/types';
import {
  PageHeader,
  Card,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  TextInput,
  CurrencyInput,
  TagsInput,
  RichTextInput,
  Select,
  Switch,
  Modal,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

const NameCell = styled.div`
  display: flex;
  flex-direction: column;

  strong {
    font-size: 0.9rem;
  }
  span {
    font-size: 0.75rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const Flags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0.6rem 0;

  span {
    font-size: 0.85rem;
    color: ${(p) => p.theme.textSecondary};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

/**
 * Seletor dos dias de resgate. Usa checkbox de verdade (escondido) com label
 * clicável: o teclado e o leitor de tela funcionam de graça, o que um <div>
 * com onClick não daria.
 */
const DayPicker = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
`;

const DayOption = styled.label<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.7rem;
  border: 1px solid ${(p) => (p.$checked ? p.theme.primary : p.theme.border)};
  border-radius: 999px;
  background: ${(p) => (p.$checked ? `${p.theme.primary}14` : 'transparent')};
  color: ${(p) => (p.$checked ? p.theme.primary : p.theme.textSecondary)};
  font-size: 0.82rem;
  font-weight: ${(p) => (p.$checked ? 600 : 500)};
  cursor: pointer;
  user-select: none;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${(p) => p.theme.primary};
  }

  &:focus-within {
    outline: 2px solid ${(p) => p.theme.primary};
    outline-offset: 2px;
  }

  input {
    /* Acessível, mas fora da tela: o visual é o da pílula. */
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
  }
`;

const DayHint = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.text};
  }
`;

const SectionTitle = styled.h4`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${(p) => p.theme.textSecondary};
  margin: 1.25rem 0 0.4rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${(p) => p.theme.border};
`;

const BILLING_OPTIONS = [
  { value: 'MONTHLY', label: 'Mensal' },
  { value: 'QUARTERLY', label: 'Trimestral' },
  { value: 'YEARLY', label: 'Anual' },
];

const BILLING_LABELS: Record<BillingPeriod, string> = {
  MONTHLY: '/mês',
  QUARTERLY: '/trimestre',
  YEARLY: '/ano',
};

/** Local form shape: features are edited as chips via TagsInput. */
interface PassPlanForm {
  code: string;
  name: string;
  description: string;
  active: boolean;
  billingPeriod: BillingPeriod;
  price: number;
  isDefault: boolean;
  publiclyVisible: boolean;
  highlighted: boolean;
  displayOrder: number;
  monthlyRedemptionLimit: number;
  unlimitedRedemptions: boolean;
  cashbackBonusPercent: number;
  adFree: boolean;
  earlyAccessPromotions: boolean;
  prioritySupport: boolean;
  features: string[];
  redemptionDays: WeekdayCode[];
}

const EMPTY_FORM: PassPlanForm = {
  code: '',
  name: '',
  description: '',
  active: true,
  billingPeriod: 'MONTHLY',
  price: 0,
  isDefault: false,
  publiclyVisible: true,
  highlighted: false,
  displayOrder: 0,
  monthlyRedemptionLimit: 5,
  unlimitedRedemptions: false,
  cashbackBonusPercent: 0,
  adFree: false,
  earlyAccessPromotions: false,
  prioritySupport: false,
  features: [],
  // Plano novo nasce liberando a semana inteira: restringir é decisão comercial
  // explícita, não o padrão.
  redemptionDays: WEEKDAYS.map((day) => day.code),
};

/**
 * Gestão do Kardume Pass: os planos do consumidor (tiers escolhidos no
 * cadastro do app/site, com limite mensal de resgate de cupom/QR Code).
 */
const PassPlansPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PassPlanForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const { data, loading, error, reload } = useAsync(() => backofficeApi.listPassPlans({ size: 100 }), []);

  const openCreate = (): void => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setIsOpen(true);
  };

  const openEdit = (plan: PassPlanDto): void => {
    setEditingId(plan.id);
    setForm({
      code: plan.code,
      name: plan.name,
      description: plan.description ?? '',
      active: plan.active,
      billingPeriod: plan.billingPeriod,
      price: plan.price,
      isDefault: plan.isDefault,
      publiclyVisible: plan.publiclyVisible,
      highlighted: plan.highlighted,
      displayOrder: plan.displayOrder,
      monthlyRedemptionLimit: plan.monthlyRedemptionLimit ?? 0,
      unlimitedRedemptions: plan.unlimitedRedemptions,
      cashbackBonusPercent: plan.cashbackBonusPercent,
      adFree: plan.adFree,
      earlyAccessPromotions: plan.earlyAccessPromotions,
      prioritySupport: plan.prioritySupport,
      features: plan.features,
      // Plano salvo antes desta feature vem com a semana completa do backfill.
      redemptionDays: plan.redemptionDays ?? WEEKDAYS.map((day) => day.code),
    });
    setIsOpen(true);
  };

  const update = <K extends keyof PassPlanForm>(key: K, value: PassPlanForm[K]): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  /** Liga/desliga um dia, mantendo a ordem segunda → domingo do contrato. */
  const toggleDay = (code: WeekdayCode): void => {
    setForm((prev) => {
      const selected = new Set(prev.redemptionDays);
      if (selected.has(code)) selected.delete(code);
      else selected.add(code);
      return {
        ...prev,
        redemptionDays: WEEKDAYS.filter((day) => selected.has(day.code)).map((day) => day.code),
      };
    });
  };

  /**
   * Prévia do rótulo que o cliente vai ver. Espelha RedemptionDays.label do
   * backend (que é a fonte da verdade) - aqui só para o operador do backoffice
   * conferir o texto antes de salvar.
   */
  const daysPreview = (): string => {
    const selected = WEEKDAYS.filter((day) => form.redemptionDays.includes(day.code));
    if (selected.length === 0) return 'Nenhum dia selecionado';
    if (selected.length === WEEKDAYS.length) return 'Todos os dias';
    if (selected.length === 1) return selected[0].label;
    const first = WEEKDAYS.findIndex((day) => day.code === selected[0].code);
    const last = WEEKDAYS.findIndex((day) => day.code === selected[selected.length - 1].code);
    const isContiguous = last - first + 1 === selected.length;
    if (isContiguous) return `${selected[0].label} a ${selected[selected.length - 1].label.toLowerCase()}`;
    const labels = selected.map((day) => day.label);
    return `${labels.slice(0, -1).join(', ')} e ${labels[labels.length - 1].toLowerCase()}`;
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    const payload: PassPlanRequestDto = {
      code: form.code,
      name: form.name,
      description: form.description || undefined,
      active: form.active,
      billingPeriod: form.billingPeriod,
      price: form.price,
      isDefault: form.isDefault,
      publiclyVisible: form.publiclyVisible,
      highlighted: form.highlighted,
      displayOrder: form.displayOrder,
      monthlyRedemptionLimit: form.unlimitedRedemptions ? undefined : form.monthlyRedemptionLimit,
      unlimitedRedemptions: form.unlimitedRedemptions,
      cashbackBonusPercent: form.cashbackBonusPercent,
      adFree: form.adFree,
      earlyAccessPromotions: form.earlyAccessPromotions,
      prioritySupport: form.prioritySupport,
      features: form.features,
      redemptionDays: form.redemptionDays,
    };
    try {
      if (editingId) {
        await backofficeApi.updatePassPlan(editingId, payload);
        showNotification('Plano atualizado com sucesso.', 'success');
      } else {
        await backofficeApi.createPassPlan(payload);
        showNotification('Plano criado com sucesso.', 'success');
      }
      setIsOpen(false);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar o plano.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const changeStatus = async (plan: PassPlanDto, action: 'activate' | 'archive'): Promise<void> => {
    try {
      await backofficeApi.setPassPlanStatus(plan.id, action);
      showNotification(action === 'activate' ? 'Plano ativado.' : 'Plano arquivado.', 'success');
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível alterar o status.'), 'error');
    }
  };

  return (
    <>
      <PageHeader
        title="Kardume Pass"
        subtitle="Planos do consumidor: limite de resgate de cupom/QR Code por mês e benefícios de cada tier."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Novo plano
          </Button>
        }
      />

      <DataState
        loading={loading}
        error={error}
        isEmpty={!data || data.content.length === 0}
        emptyTitle="Nenhum plano cadastrado"
        emptyDescription="Crie o plano padrão (gratuito) e os tiers pagos do Kardume Pass."
        onRetry={reload}
      >
        {data && (
          <Card>
            <TableContainer>
              <Table>
                <thead>
                  <tr>
                    <th>Plano</th>
                    <th>Preço</th>
                    <th>Situação</th>
                    <th>Resgates/mês</th>
                    <th>Dias de resgate</th>
                    <th>Sinalizadores</th>
                    <th>Assinantes</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.content.map((plan) => (
                    <tr key={plan.id}>
                      <td>
                        <NameCell>
                          <strong>{plan.name}</strong>
                          <span>{plan.code}</span>
                        </NameCell>
                      </td>
                      <td>
                        {plan.price > 0 ? formatCurrency(plan.price) : 'Grátis'}
                        {plan.price > 0 && <span> {BILLING_LABELS[plan.billingPeriod]}</span>}
                      </td>
                      <td>
                        <Badge tone={plan.active ? 'success' : 'secondary'}>
                          {plan.active ? 'Ativo' : 'Arquivado'}
                        </Badge>
                      </td>
                      <td>{plan.unlimitedRedemptions ? 'Ilimitado' : formatNumber(plan.monthlyRedemptionLimit ?? 0)}</td>
                      <td>{plan.redemptionDaysLabel ?? 'Todos os dias'}</td>
                      <td>
                        <Flags>
                          {plan.isDefault && <Badge tone="info">Padrão</Badge>}
                          {plan.publiclyVisible && <Badge tone="info">Público</Badge>}
                          {plan.highlighted && <Badge tone="primary">Destaque</Badge>}
                        </Flags>
                      </td>
                      <td>{formatNumber(plan.subscribersCount ?? 0)}</td>
                      <td>
                        <RowActions>
                          <ActionButton aria-label="Editar plano" onClick={() => openEdit(plan)}>
                            <Edit3 size={16} />
                          </ActionButton>
                          {plan.active ? (
                            <ActionButton aria-label="Arquivar plano" onClick={() => changeStatus(plan, 'archive')}>
                              <Archive size={16} />
                            </ActionButton>
                          ) : (
                            <ActionButton aria-label="Ativar plano" onClick={() => changeStatus(plan, 'activate')}>
                              <Play size={16} />
                            </ActionButton>
                          )}
                        </RowActions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </DataState>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editingId ? 'Editar plano' : 'Novo plano'}
        width="640px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={submit} isLoading={submitting}>
              Salvar
            </Button>
          </>
        }
      >
        <FormRow>
          <TextInput
            label="Código"
            value={form.code}
            onChange={(e) => update('code', e.target.value)}
            placeholder="Ex.: FREE, PREMIUM"
          />
          <TextInput
            label="Nome"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            placeholder="Ex.: Kardume Clube"
          />
        </FormRow>
        {/* Editor rico, e é o que a tela de escolha de plano renderiza: o card
            passa a descrição por `sanitizeRichHtml` e desenha com HTML, então
            negrito, lista e citação que o operador escrever aparecem para o
            cliente. Voltar isto para texto puro quebraria aquilo. */}
        <RichTextInput
          label="Descrição"
          value={form.description}
          onChange={(html) => update('description', html)}
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <FormRow>
          <Select
            label="Periodicidade"
            options={BILLING_OPTIONS}
            value={form.billingPeriod}
            onChange={(e) => update('billingPeriod', e.target.value as BillingPeriod)}
          />
          <CurrencyInput
            label="Preço"
            value={form.price}
            onChange={(value) => update('price', value ?? 0)}
            helpText="0 = gratuito"
          />
        </FormRow>

        <SectionTitle>Limite de resgate</SectionTitle>
        <FormRow>
          <TextInput
            label="Resgates de cupom/QR Code por mês"
            type="number"
            value={form.monthlyRedemptionLimit}
            onChange={(e) => update('monthlyRedemptionLimit', Number(e.target.value))}
            disabled={form.unlimitedRedemptions}
          />
          <SwitchRow style={{ alignSelf: 'end' }}>
            <span>Resgates ilimitados</span>
            <Switch checked={form.unlimitedRedemptions} onChange={(v) => update('unlimitedRedemptions', v)} />
          </SwitchRow>
        </FormRow>

        <SectionTitle>Dias disponíveis para resgate</SectionTitle>
        <DayPicker>
          {WEEKDAYS.map((day) => {
            const checked = form.redemptionDays.includes(day.code);
            return (
              <DayOption key={day.code} $checked={checked}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleDay(day.code)}
                />
                {day.label}
              </DayOption>
            );
          })}
        </DayPicker>
        <DayHint>
          O cliente deste plano só resgata cupom/QR Code nos dias marcados. Vai aparecer no site
          e no app como <strong>{daysPreview()}</strong>.
        </DayHint>

        <SectionTitle>Benefícios extras</SectionTitle>
        <TextInput
          label="Bônus de cashback (pontos percentuais)"
          type="number"
          value={form.cashbackBonusPercent}
          onChange={(e) => update('cashbackBonusPercent', Number(e.target.value))}
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <SwitchRow>
          <span>Experiência sem anúncios</span>
          <Switch checked={form.adFree} onChange={(v) => update('adFree', v)} />
        </SwitchRow>
        <SwitchRow>
          <span>Acesso antecipado a promoções</span>
          <Switch checked={form.earlyAccessPromotions} onChange={(v) => update('earlyAccessPromotions', v)} />
        </SwitchRow>
        <SwitchRow>
          <span>Suporte prioritário</span>
          <Switch checked={form.prioritySupport} onChange={(v) => update('prioritySupport', v)} />
        </SwitchRow>
        <TagsInput
          label="Recursos"
          value={form.features}
          onChange={(tags) => update('features', tags)}
          placeholder="Digite um recurso e pressione Enter..."
          helpText="Lista livre exibida na vitrine de planos do cadastro."
          containerStyle={{ marginTop: '0.75rem' }}
        />

        <SectionTitle>Vitrine e catálogo</SectionTitle>
        <TextInput
          label="Ordem de exibição"
          type="number"
          value={form.displayOrder}
          onChange={(e) => update('displayOrder', Number(e.target.value))}
        />
        <SwitchRow>
          <span>Plano padrão (atribuído a quem não escolhe nenhum no cadastro)</span>
          <Switch checked={form.isDefault} onChange={(v) => update('isDefault', v)} />
        </SwitchRow>
        <SwitchRow>
          <span>Visível publicamente</span>
          <Switch checked={form.publiclyVisible} onChange={(v) => update('publiclyVisible', v)} />
        </SwitchRow>
        <SwitchRow>
          <span>Plano em destaque</span>
          <Switch checked={form.highlighted} onChange={(v) => update('highlighted', v)} />
        </SwitchRow>
      </Modal>
    </>
  );
};

export default PassPlansPage;
